import { useCallback, useEffect, useState } from 'react';

import { getStorage, setStorage } from '../utils/storage_service';

export const KEYS = {
  LG: 'lg',
  MD: 'md',
  SM: 'sm',
  XS: 'xs',
  XXS: 'xxs',
};

const useLayout = (layoutKey: string, defaultLayout: any) => {
  const [layouts, setLayouts] = useState(getStorage(layoutKey) || defaultLayout);
  const [maximized, setMaximized] = useState('');
  const [prevLayouts, setPrevLayouts] = useState<any | null>(null);
  const [showVolSurfaceSmile, setShowVolSurfaceSmile] = useState(false);
  const saveLayouts = useCallback(() => {
    if (!maximized) {
      setStorage(layoutKey, layouts);
    }
  }, [layoutKey, layouts, maximized]);

  useEffect(() => {
    saveLayouts();
  }, [layouts, saveLayouts]);

  const maximizeCard = useCallback(
    (card: string) => {
      const COLS_BY_KEY: Record<string, number> = {
        lg: 12,
        md: 12,
        sm: 12,
        xs: 1,
        xxs: 1,
      };

      if (!maximized) {
        const cloned: any = JSON.parse(JSON.stringify(layouts));

        Object.keys(cloned).forEach((key) => {
          const cols = COLS_BY_KEY[key] ?? COLS_BY_KEY.lg;
          cloned[key] = cloned[key].map((item: any) => {
            if (card === item.i) {
              return {
                ...item,
                static: true,
                x: 0,
                w: cols,
              };
            }
            return item;
          });
        });

        setPrevLayouts(layouts);
        setMaximized(card);
        setLayouts(cloned);
        return;
      }

      setMaximized('');
      if (prevLayouts) {
        setLayouts(prevLayouts);
      } else {
        const arr = JSON.parse(JSON.stringify(layouts));
        Object.keys(arr).forEach((key: string) => {
          arr[key] = arr[key].map((item: any) =>
            card === item.i
              ? {
                  ...item,
                  static: false,
                }
              : item,
          );
        });
        setLayouts(arr);
      }
      setPrevLayouts(null);
    },
    [layouts, maximized, prevLayouts],
  );

  const changeLayoutHeight = useCallback(
    (card: string, values: { size: string; value: number }[]) => {
      const arr = { ...layouts };

      Object.keys(arr).forEach((key) => {
        arr[key] = arr[key].map((item: any) => {
          const valueItem = values.find((v: { size: string; value: number }) => v.size === key);
          if (card === item.i && !!valueItem) {
            return {
              ...item,
              h: valueItem.value,
            };
          }

          return item;
        });
      });

      setLayouts(arr);
    },
    [layouts],
  );

  const updateVolSurfaceSmilesLayout = useCallback(
    (values: any[]) => {
      const arr = { ...layouts };

      Object.keys(arr).forEach((key) => {
        arr[key] = arr[key].map((item: any) => {
          const valueItem = values.find((v: { size: string; value: number }) => v.size === key);
          if ('snapshot-data' === item.i && !!valueItem) {
            return {
              ...item,
              h: valueItem.value,
            };
          }

          return item;
        });
      });

      setLayouts(arr);
    },
    [layouts],
  );

  const resetLayout = useCallback(() => {
    if (showVolSurfaceSmile) {
      const values = [
        { size: 'lg', value: 7 },
        { size: 'md', value: 7 },
        { size: 'sm', value: 8 },
        { size: 'xs', value: 8 },
        { size: 'xxs', value: 8 },
      ];

      updateVolSurfaceSmilesLayout(values);
      return;
    }

    setLayouts(defaultLayout);
  }, [defaultLayout, showVolSurfaceSmile, updateVolSurfaceSmilesLayout]);

  const fixVolSurfaceSmiles = useCallback(() => {
    if (!showVolSurfaceSmile) {
      const values = [
        { size: 'lg', value: 4 },
        { size: 'md', value: 4 },
        { size: 'sm', value: 4 },
        { size: 'xs', value: 4 },
        { size: 'xxs', value: 4 },
      ];
      updateVolSurfaceSmilesLayout(values);
    }
  }, [showVolSurfaceSmile]);

  useEffect(() => {
    fixVolSurfaceSmiles();
  }, [fixVolSurfaceSmiles]);

  return {
    layouts,
    setLayouts,
    resetLayout,
    maximized,
    setMaximized,
    maximizeCard,
    changeLayoutHeight,
    showVolSurfaceSmile,
    setShowVolSurfaceSmile,
  };
};

export default useLayout;
